import { useCallback, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';

import { useOnlineStatus } from '../components/Hooks/useOnlineStatus';
import useShiftTimes from '../components/Hooks/useShiftTimes';
import { useSignalRContext } from '../components/Contexts/SignalRContext';
import { ErrorResponse } from '../DTOs/Common/ErrorResponse';
import { geologyAjax } from '../ajax/GeologyAjax';
import { operationsAjax } from '../ajax/OperationsAjax';
import { stoppagesAjax } from '../ajax/StoppagesAjax';
import { OptionResponse } from '../DTOs/Common/OptionResponse';
import { plantAjax } from '../ajax/PlantAjax';
import { diggingAjax } from '../ajax/DiggingAjax';
import { referencesAjax } from '../ajax/ReferencesAjax';
import { productionAjax } from '../ajax/ProductionAjax';
import { PlantDTO } from '../DTOs/Plants/PlantDTO';
import { DiggingLocationDTO } from '../DTOs/Production/DiggingLocationDTO';
import { StoppageCalc } from '../DTOs/Stoppage/StoppageCalc';
import { PagedResult } from '../DTOs/Common/PagedResult';

const STALE_MS = 5 * 60 * 1000;

function extractItems(res: any): any[] {
  if (Array.isArray(res)) return res;
  if (res && Array.isArray(res.data)) return res.data;
  return [];
}

export function usePrefetchCriticalPages() {
  const queryClient = useQueryClient();
  const isOnline = useOnlineStatus();
  const { selectedDate, selectedShift } = useShiftTimes();
  const { connection } = useSignalRContext();

  const runPrefetches = useCallback((staleTime: number) => {
    if (!selectedDate?.isValid) return;

    const date = selectedDate;
    const shift = selectedShift;
    const dateStr = date.toISODate()!;

    const pq = (key: unknown[], fn: () => Promise<unknown>) =>
      queryClient.prefetchQuery({ queryKey: key, queryFn: fn, staleTime });

    const pqOptions = (key: unknown[], fn: () => Promise<any>) =>
      pq(key, async () => {
        const res = await fn();
        return ErrorResponse.isErrorResponse(res) ? [] : extractItems(res);
      });

    pqOptions(['geology', 'rehandleDiggers', dateStr, shift], () => geologyAjax.getRehandleDiggers(date, shift));
    pqOptions(['geology', 'rehandleTrucks', dateStr], () => geologyAjax.getRehandleTruckingTrucks(date));
    pqOptions(['geology', 'rehandleFromLocations', dateStr], () => geologyAjax.getRehandleMovementFromLocations(date));
    pqOptions(['geology', 'rehandleToLocations', dateStr], () => geologyAjax.getRehandleMovementToLocations(date));

    pq(
      ['dataTable', 'geology', 'rehandles', dateStr, shift, 1, 15, null, 'asc', false, null],
      async () => {
        const res = await geologyAjax.getGeoRehandles(date, shift);
        if (ErrorResponse.isErrorResponse(res)) throw new Error(res.messages.join(', '));
        return res;
      }
    );

    pqOptions(['geology', 'crushingFromLocations', dateStr], () => geologyAjax.getCrushingMovementFromLocations(date));
    pqOptions(['geology', 'crushingToLocations', dateStr], () => geologyAjax.getCrushingMovementToLocations(date));
    pqOptions(['geology', 'crushingLoaders', dateStr, shift], () => geologyAjax.getCrushingLoaders(date, shift));

    pq(
      ['dataTable', 'geology', 'crushing', dateStr, shift, 1, 5, null, 'asc', false, null],
      async () => {
        const res = await geologyAjax.getGeoCrushingEntries(date, shift);
        if (ErrorResponse.isErrorResponse(res)) throw new Error(res.messages.join(', '));
        return res;
      }
    );

    pq(['references', 'stoppageTypes'], async () => {
      const res = await stoppagesAjax.getStoppageTypes();
      return ErrorResponse.isErrorResponse(res) ? new OptionResponse([]) : res;
    });
    pqOptions(['references', 'jobTypes'], () => referencesAjax.getJobTypes());
    pqOptions(['references', 'locations'], () => referencesAjax.getLocations({} as any));
    pqOptions(['references', 'departments'], () => referencesAjax.getDepartments({ pageNumber: 1, pageSize: 100 } as any));

    pq(
      ['dataTable', 'stoppages', 'list', dateStr, shift, 'Down & Up', null, '', null, 1, 15, null, 'asc', false, null],
      async () => {
        const res = await stoppagesAjax.getStoppagesMaintenance({
          pageNumber: 1, pageSize: 15,
          shiftDate: dateStr, dayOrNight: shift[0],
          status: '', machineName: '',
        } as any);
        if (ErrorResponse.isErrorResponse(res)) throw new Error(res.messages.join(', '));
        return res;
      }
    );

    const defaultOpFilters = [{ key: 'Status', value: '' }, { key: 'MachineName', value: '' }];
    const opKey = ['dataTable', 'operations', 'stoppageCalcs', dateStr, shift, defaultOpFilters, 1, 5, null, 'asc', false, null];
    (async () => {
      await queryClient.prefetchQuery({
        queryKey: opKey,
        queryFn: async () => {
          const res = await operationsAjax.getStoppageCalcsData(date, shift, { Status: '', MachineName: '', pageNumber: 0, pageSize: 10 });
          if (ErrorResponse.isErrorResponse(res)) throw new Error(res.messages.join(', '));
          return res;
        },
        staleTime,
      });
      const calcs = queryClient.getQueryData<PagedResult<StoppageCalc>>(opKey);
      for (const entry of calcs?.data ?? []) {
        pq(
          ['dataTable', 'operations', 'plantStoppages', dateStr, shift, entry.plantId, entry.status, 1, 5, null, 'asc', false, null],
          async () => {
            const res = await stoppagesAjax.getStoppagesByShiftAndPlant(date, shift, entry.plantId);
            if (ErrorResponse.isErrorResponse(res)) throw new Error(res.messages.join(', '));
            return res;
          }
        );
      }
    })();

    pq(
      ['dataTable', 'chatree', 'diggingLoadingSummary', dateStr, shift, 1, 10, null, 'asc', false, null],
      async () => {
        const res = await diggingAjax.getDiggingLoadingSummary(date, shift, { pageNumber: 1, pageSize: 10 });
        if (ErrorResponse.isErrorResponse(res)) throw new Error(res.messages.join(', '));
        return res;
      }
    );

    pqOptions(['production', 'locationOptions', dateStr, shift], () => diggingAjax.getLocationOptions(date, shift));
    pqOptions(['geology', 'productionTrucks', dateStr], () => geologyAjax.getProductionTruckingTrucks(date));

    (async () => {
      try {
        const plants = await queryClient.fetchQuery<PlantDTO[]>({
          queryKey: ['chatree', 'plants', dateStr, shift],
          queryFn: async () => {
            const res = await plantAjax.getAvailableDiggers(date, shift);
            return (ErrorResponse.isErrorResponse(res) ? [] : extractItems(res)) as PlantDTO[];
          },
          staleTime,
        });

        await Promise.all(
          plants.map(plant =>
            queryClient.fetchQuery<PagedResult<DiggingLocationDTO>>({
              queryKey: ['dataTable', 'chatree', 'diggerLocations', dateStr, shift, plant.id, 1, 5, null, 'asc', false, null],
              queryFn: async () => {
                const res = await diggingAjax.getDiggerLocations(date, shift, plant.id);
                if (ErrorResponse.isErrorResponse(res)) throw new Error(res.messages.join(', '));
                return res;
              },
              staleTime,
            }).catch(() => null)
          )
        );

        // Note: one shift-wide request warms every per-pattern-per-hour truck-loads cache, instead
        // of firing pattern-count x 12 individual requests.
        const shiftLoadGroups = await diggingAjax.getTruckLoadsForShift(date, shift, { pageNumber: 1, pageSize: 5 });
        if (!ErrorResponse.isErrorResponse(shiftLoadGroups)) {
          for (const group of shiftLoadGroups) {
            queryClient.setQueryData(
              ['dataTable', 'chatree', 'truckLoads', group.diggingPatternId, group.hour, 1, 5, null, 'asc', false, null],
              group.result
            );
          }
        }
      } catch {
        // ignore - offline or server error
      }
    })();

    pq(['production', 'allowedNonWasteLocationTypeIds'], async () => {
      const res = await productionAjax.getAllowedNonWasteLocationTypeIds();
      return ErrorResponse.isErrorResponse(res) ? [] : res;
    });
    pq(['production', 'allowedWasteLocationTypeIds'], async () => {
      const res = await productionAjax.getAllowedWasteLocationTypeIds();
      return ErrorResponse.isErrorResponse(res) ? [] : res;
    });
    pqOptions(['references', 'blastmasterIds', dateStr, shift], () => diggingAjax.getBlastmasterIds(date, shift));
    pq(['references', 'pitStages'], async () => {
      const res = await referencesAjax.getPitStages({ pageNumber: 1, pageSize: 100 } as any);
      if (ErrorResponse.isErrorResponse(res)) return [];
      return res.data;
    });
    pq(['references', 'materialTypes', { nonWasteOnly: false, isLive: true }], async () => {
      const res = await referencesAjax.getMaterialTypes({ nonWasteOnly: false, isLive: true, pageNumber: 1, pageSize: 100 } as any);
      if (ErrorResponse.isErrorResponse(res)) return [];
      return res.data;
    });
    pq(['references', 'materialTypeClasses', { isLive: true }], async () => {
      const res = await referencesAjax.getMaterialTypeClasses({ isLive: true, pageNumber: 1, pageSize: 100 } as any);
      if (ErrorResponse.isErrorResponse(res)) return [];
      return res.data;
    });
  }, [queryClient, selectedDate, selectedShift]);

  useEffect(() => {
    if (!isOnline || !selectedDate?.isValid) return;
    // Note: warm the offline cache during idle time so it does not compete with the initial
    // page render / dashboard load. Falls back to a short timeout where requestIdleCallback
    // is unavailable.
    const w = window as Window & {
      requestIdleCallback?: (cb: () => void) => number;
      cancelIdleCallback?: (handle: number) => void;
    };
    const schedule = w.requestIdleCallback ?? ((cb: () => void) => window.setTimeout(cb, 200) as unknown as number);
    const cancel = w.cancelIdleCallback ?? ((handle: number) => window.clearTimeout(handle));
    const handle = schedule(() => runPrefetches(STALE_MS));
    return () => cancel(handle);
  }, [isOnline, selectedDate, selectedShift, runPrefetches]);

  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!connection) return;

    const refresh = () => {
      if (!selectedDate?.hasSame(DateTime.now(), 'day')) return;
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = setTimeout(() => runPrefetches(0), 2000);
    };

    connection.on('DataUpdatedAsync', refresh);
    connection.on('DataInsertedAsync', refresh);
    connection.on('DataDeletedAsync', refresh);

    return () => {
      connection.off('DataUpdatedAsync', refresh);
      connection.off('DataInsertedAsync', refresh);
      connection.off('DataDeletedAsync', refresh);
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, [connection, selectedDate, runPrefetches]);
}
